import { isTauriRuntime } from '@/helpers/isTauriRuntime'
import { getPlatform } from '@/platform'

const PLUGIN = 'lucimark-android'

function isAndroidTauri(): boolean {
  return isTauriRuntime() && getPlatform() === 'android'
}

async function invokePlugin<T>(command: string, args?: Record<string, unknown>): Promise<T | null> {
  if (!isAndroidTauri()) {
    return null
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return args == null
      ? await invoke<T>(`plugin:${PLUGIN}|${command}`)
      : await invoke<T>(`plugin:${PLUGIN}|${command}`, args)
  }
  catch {
    return null
  }
}

/**
 * Last-known Accessibility enablement from the native bridge.
 * `null` = invoke failed / unavailable (do not treat as off for telemetry).
 */
export async function probeAccessibilityServiceEnabled(): Promise<boolean | null> {
  const raw = await invokePlugin<unknown>('isAccessibilityServiceEnabled')
  if (typeof raw === 'boolean') {
    return raw
  }
  if (raw != null && typeof raw === 'object' && 'enabled' in raw) {
    return (raw as { enabled: unknown }).enabled === true
  }
  return null
}

/** True when Lucimark Accessibility service is enabled (Android Device Owner path). Web / bridge fail → false. */
export async function isAccessibilityServiceEnabled(): Promise<boolean> {
  return (await probeAccessibilityServiceEnabled()) === true
}

/** Opens Android app-info settings for this package. No-op on web / missing bridge. */
export async function openAppInfo(): Promise<boolean> {
  const ok = await invokePlugin<unknown>('openAppInfo')
  return ok !== null
}

/** Opens system Accessibility settings. No-op on web / missing bridge. */
export async function openAccessibilitySettings(): Promise<boolean> {
  const ok = await invokePlugin<unknown>('openAccessibilitySettings')
  return ok !== null
}

/**
 * Last-known Device Owner from the native bridge.
 * `null` = invoke failed / unavailable (do not treat as non-DO for telemetry).
 */
export async function probeDeviceOwnerApp(): Promise<boolean | null> {
  const raw = await invokePlugin<unknown>('isDeviceOwnerApp')
  if (typeof raw === 'boolean') {
    return raw
  }
  if (raw != null && typeof raw === 'object' && 'deviceOwner' in raw) {
    return (raw as { deviceOwner: unknown }).deviceOwner === true
  }
  return null
}

/** True when this install is Device Owner. Web / bridge fail → false. */
export async function isDeviceOwnerApp(): Promise<boolean> {
  return (await probeDeviceOwnerApp()) === true
}

export interface TRemoteScreenshotResult {
  jpegBase64: string
  width?: number
  height?: number
}

function parseScreenshot(raw: unknown): TRemoteScreenshotResult | null {
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  const jpeg
    = typeof o.jpegBase64 === 'string'
      ? o.jpegBase64
      : typeof o.frameJpegBase64 === 'string'
        ? o.frameJpegBase64
        : null
  if (jpeg == null || jpeg === '') {
    return null
  }
  const out: TRemoteScreenshotResult = { jpegBase64: jpeg }
  if (typeof o.width === 'number' && o.width > 0) {
    out.width = o.width
  }
  if (typeof o.height === 'number' && o.height > 0) {
    out.height = o.height
  }
  if (typeof o.frameWidth === 'number' && o.frameWidth > 0) {
    out.width = o.frameWidth
  }
  if (typeof o.frameHeight === 'number' && o.frameHeight > 0) {
    out.height = o.frameHeight
  }
  return out
}

/** Capture a remote-view JPEG frame via Accessibility. Null when unavailable. */
export async function remoteTakeScreenshot(): Promise<TRemoteScreenshotResult | null> {
  return parseScreenshot(await invokePlugin<unknown>('remoteTakeScreenshot'))
}

/** Normalized [0,1] tap via Accessibility. */
export async function remoteTap(x: number, y: number): Promise<boolean> {
  const raw = await invokePlugin<unknown>('remoteTap', { x, y })
  if (typeof raw === 'boolean') {
    return raw
  }
  if (raw != null && typeof raw === 'object' && 'ok' in raw) {
    return (raw as { ok: unknown }).ok === true
  }
  // Empty resolve() can arrive as null/undefined — treat as success only when invoke did not throw.
  // invokePlugin returns null on reject; distinguish by requiring an object/boolean payload.
  return false
}

/** Normalized [0,1] swipe via Accessibility. */
export async function remoteSwipe(params: {
  x1: number
  y1: number
  x2: number
  y2: number
  durationMs?: number
}): Promise<boolean> {
  const raw = await invokePlugin<unknown>('remoteSwipe', params)
  if (typeof raw === 'boolean') {
    return raw
  }
  if (raw != null && typeof raw === 'object' && 'ok' in raw) {
    return (raw as { ok: unknown }).ok === true
  }
  return false
}
